import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, FileDown, Copy, Lock, Pencil, Loader2 } from "lucide-react";
import { FAILURE_POINTS } from "@/lib/constants";
import { JackstoneLogo } from "@/components/Logo";
import { useRole } from "@/lib/roles";
import RelatedTests from "@/components/RelatedTests";
import PhotosSection from "@/components/PhotosSection";
import PdfLoadingOverlay from "@/components/PdfLoadingOverlay";
import { toast } from "sonner";

export default function Certificate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canWrite } = useRole();
  const [test, setTest] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    // Independent loads — a failing /employees call must not stop the certificate
    // (or its data) from rendering, and vice-versa.
    api.get(`/tests/${id}`)
      .then((t) => setTest(t.data))
      .catch((e) => console.error("certificate test load failed", e));
    api.get("/employees")
      .then((e) => setEmployees(e.data))
      .catch((e) => console.error("certificate employees load failed", e));
  }, [id]);

  const empName = (eid) => employees.find((e) => e.id === eid)?.name || "—";

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const token = localStorage.getItem("jackstone_token");
      const res = await fetch(`${API}/tests/${id}/certificate.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("PDF generation failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `JST-${id.slice(0, 8).toUpperCase()}-certificate.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Could not generate the certificate PDF. Please try again.");
    } finally {
      setPdfLoading(false);
    }
  };

  if (!test) {
    return (
      <div className="p-10 text-sm text-[#50493b]">Loading certificate…</div>
    );
  }

  const fpLabel = (code) =>
    FAILURE_POINTS.find((fp) => fp.code === code)?.label || code;

  return (
    <div className="p-6 md:p-10 print-page bg-slate-100 min-h-screen">
      <div className="no-print max-w-4xl mx-auto flex items-center justify-between mb-6">
        <Link
          to="/tests"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#50493b] hover:text-[#39678f]"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to registry
        </Link>
        <div className="flex items-center gap-2">
          {canWrite && test.blown_out_employee_id && (
            <Button
              data-testid="cert-retest-btn"
              onClick={() => navigate(`/tests/new?clone_from=${id}`)}
              variant="outline"
              className="border-[#39678f]/50 text-[#39678f] hover:bg-[#e6f3ff] subtitle uppercase tracking-wider text-xs"
            >
              <Copy className="w-4 h-4 mr-2" /> Log re-test
            </Button>
          )}
          {canWrite && !test.blown_out_employee_id && (
            <Button
              data-testid="cert-edit-btn"
              onClick={() => navigate(`/tests/${id}`)}
              variant="outline"
              className="border-[#39678f]/50 text-[#39678f] hover:bg-[#e6f3ff] subtitle uppercase tracking-wider text-xs"
            >
              <Pencil className="w-4 h-4 mr-2" /> Edit record
            </Button>
          )}
          <Button
            data-testid="download-pdf-btn"
            onClick={downloadPdf}
            disabled={pdfLoading}
            className="bg-[#39678f] hover:bg-[#2c5272] text-white subtitle uppercase tracking-wider text-xs disabled:opacity-60"
          >
            {pdfLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileDown className="w-4 h-4 mr-2" />
            )}
            {pdfLoading ? "Generating…" : "Download PDF"}
          </Button>
          <Button
            data-testid="print-certificate-btn"
            onClick={() => window.print()}
            variant="outline"
            className="border-[#33424f]/30 text-[#33424f] hover:bg-[#f5f1ec] subtitle uppercase tracking-wider text-xs"
          >
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
        </div>
      </div>

      {test.blown_out_employee_id && (
        <div
          data-testid="cert-lock-banner"
          className="no-print max-w-4xl mx-auto mb-4 border border-[#33424f] bg-[#33424f] text-white p-3 flex items-center gap-3 text-xs"
        >
          <Lock className="w-3.5 h-3.5 text-[#ccb795] shrink-0" />
          <div className="font-body">
            <span className="subtitle uppercase tracking-[0.15em] text-[10px] text-[#ccb795] mr-2">Locked</span>
            Record final · Blown Out
            {test.blown_out_date ? ` on ${test.blown_out_date}` : ""}. Log a new re-test for any repair or replacement.
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto bg-white border border-[#33424f]/20 p-10 print:border-0">
        {/* Header */}
        <div className="flex items-start justify-between border-b-2 border-[#33424f] pb-6 mb-6">
          <div>
            <JackstoneLogo className="h-9 w-auto" variant="brand" />
            <h1 className="font-display text-4xl tracking-tight text-[#33424f] leading-none mt-6">
              PRESSURE TEST CERTIFICATE
            </h1>
            <div className="font-body text-xs text-[#50493b] mt-2">
              Issued under PED 2014/68/EU — Annex I, §7.4
            </div>
          </div>
          <div className="text-right">
            <div className="subtitle text-[11px] uppercase tracking-[0.2em] text-[#50493b]">
              Certificate №
            </div>
            <div className="mono subtitle text-lg text-[#33424f]">
              JST-{test.id.slice(0, 8).toUpperCase()}
            </div>

            {/* Split status stack: top = Pass / Fail / Pending (test result) ; bottom = Dried Out (finalization) */}
            <div
              data-testid="cert-status-stack"
              className="mt-4 inline-flex flex-col items-end gap-2"
            >
              <ResultBadge result={test.test_result} date={test.test_date} />
              <BlownOutBadge
                blownOut={!!test.blown_out_employee_id}
                date={test.blown_out_date}
              />
            </div>
          </div>
        </div>

        {/* Top grid */}
        <div className="grid grid-cols-2 gap-8 mb-8">
          <InfoField label="Job / Freezer" value={test.job_number} />
          <InfoField label="Test date" value={test.test_date || "—"} />
          <InfoField
            label="Vessel"
            value={`${test.vessel_type}${test.vessel_number ? ` #${test.vessel_number}` : ""}`}
          />
          <InfoField label="Test result" value={test.test_result || "—"} />
          <InfoField label="Test pressure" value={test.test_pressure || "—"} />
          <InfoField label="Test medium" value={test.test_medium || "—"} />
        </div>

        {/* Personnel */}
        <SectionHeader>Personnel</SectionHeader>
        <div className="grid grid-cols-2 gap-8 mb-8">
          <InfoField label="Welder 1" value={empName(test.welder1_id)} />
          <InfoField label="Welder 2" value={empName(test.welder2_id)} />
          <InfoField label="Test person" value={empName(test.test_person_id)} />
          <InfoField
            label="Blown out by"
            value={
              test.blown_out_employee_id
                ? `${empName(test.blown_out_employee_id)}${test.blown_out_date ? ` · ${test.blown_out_date}` : ""}`
                : "—"
            }
          />
          <InfoField
            label="Witness tested by NoBo"
            value={test.witness_tested_by_nobo ? "Yes" : "No"}
          />
        </div>

        {/* Failure points */}
        <SectionHeader>Failure / leak points observed</SectionHeader>
        {test.failure_points && test.failure_points.length > 0 ? (
          <ul className="grid grid-cols-2 gap-2 mb-8">
            {test.failure_points.map((code) => (
              <li
                key={code}
                className="flex items-center gap-3 border border-[#b30035]/40 bg-[#fbe4ea] px-3 py-2"
              >
                <span className="font-mono font-bold text-[#b30035] text-sm">{code}</span>
                <span className="text-xs text-[#33424f]">{fpLabel(code)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-sm text-[#50493b] mb-8 border border-[#33424f]/10 bg-[#f5f1ec] px-3 py-2">
            None recorded.
          </div>
        )}

        {/* Notes */}
        <SectionHeader>Notes</SectionHeader>
        <div className="text-sm text-[#33424f] whitespace-pre-wrap mb-8 min-h-[3rem] border-l-2 border-[#33424f] pl-4 font-body">
          {test.notes || "—"}
        </div>

        {/* Footer */}
        <div className="border-t-2 border-[#33424f] pt-4 grid grid-cols-2 gap-8 text-xs text-[#50493b]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#50493b]">Created</div>
            <div className="font-semibold">{test.created_by_name}</div>
            <div>{new Date(test.created_at).toLocaleString()}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#50493b]">Last modified</div>
            <div className="font-semibold">{test.modified_by_name}</div>
            <div>{new Date(test.modified_at).toLocaleString()}</div>
          </div>
        </div>
      </div>

      <RelatedTests testId={id} />

      {/* Photo evidence — shown on screen only. Excluded from browser print via
          `no-print`; the WeasyPrint PDF is server-rendered and never includes this. */}
      <div className="no-print max-w-4xl mx-auto">
        <PhotosSection testRecordId={id} locked={!!test.blown_out_employee_id} />
      </div>

      <PdfLoadingOverlay open={pdfLoading} message="Building your certificate PDF…" />
    </div>
  );
}

function ResultBadge({ result, date }) {
  const r = (result || "").trim().toLowerCase();
  let label = "Pending";
  let classes = "border-[#ccb795] bg-[#f5f1ec] text-[#50493b]";
  let dot = "bg-[#ccb795]";
  if (r === "pass" || r === "passed") {
    label = "Passed";
    classes = "border-[#2d5e1f] bg-[#e6f1df] text-[#1f4715]";
    dot = "bg-[#2d5e1f]";
  } else if (r === "fail" || r === "failed") {
    label = "Failed";
    classes = "border-[#b30035] bg-[#fbe4ea] text-[#7a0024]";
    dot = "bg-[#b30035]";
  } else if (r.startsWith("uncertain")) {
    label = "Uncertain";
    classes = "border-[#586673] bg-[#EDF0F2] text-[#586673]";
    dot = "bg-[#586673]";
  }
  return (
    <span
      data-testid={`cert-result-badge-${label.toLowerCase()}`}
      className={`inline-flex items-center gap-2 border px-3 py-1 text-[10px] subtitle uppercase tracking-[0.2em] ${classes}`}
    >
      <span className={`h-1.5 w-1.5 ${dot}`} />
      {label}{date ? ` · ${date}` : ""}
    </span>
  );
}

function BlownOutBadge({ blownOut, date }) {
  if (!blownOut) {
    return (
      <span
        data-testid="cert-blownout-badge-pending"
        className="inline-flex items-center gap-2 border border-dashed border-[#33424f]/40 bg-white px-3 py-1 text-[10px] subtitle uppercase tracking-[0.2em] text-[#50493b]"
      >
        <span className="h-1.5 w-1.5 bg-[#ccb795]" />
        Awaiting blowdown
      </span>
    );
  }
  return (
    <span
      data-testid="cert-blownout-badge-done"
      className="inline-flex items-center gap-2 border border-[#33424f] bg-[#33424f] px-3 py-1 text-[10px] subtitle uppercase tracking-[0.2em] text-white"
    >
      <Lock className="w-3 h-3 text-[#ccb795]" />
      Dried out{date ? ` · ${date}` : ""}
    </span>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#50493b] subtitle">
        {label}
      </div>
      <div className="text-base subtitle text-[#33424f] mt-1">{value}</div>
    </div>
  );
}

function SectionHeader({ children }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.3em] text-[#39678f] subtitle border-b border-[#33424f]/20 pb-1 mb-4">
      {children}
    </div>
  );
}
